import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { BrowserStorage, browserSessionStore } from '@tassat/utility';
import React, { Suspense, lazy } from 'react';

import { getAuthConfiguration } from 'config/variables';
import PageLoading from 'Containers/common/pageLoading';
import { ConstantsContext } from 'Lib/constant';
import { setGoogleAnalytics } from 'Lib/googleAnalytics/googleAnalyticsHelpers';
import { reportAppErrorEvent } from 'Lib/logging/applicationLogging';
import { ApplicationMuiTheme, createMuiTheme } from 'Lib/themes/muiTheme';
import { useFetch } from 'SharedComponents/hooks/useFetch';

const AdminRoutes = lazy(() => import('./routes-admin'));
const ClientRoutes = lazy(() => import('./routes-client'));
const MaintenancePage = lazy(() => import('./containers/common/maintenance/maintenance'));

interface AppProps {
    isAdmin: boolean;
}

const App = (props: AppProps) => {
    const { isAdmin } = props;

    const {
        isFetching: isFetchingTheme,
        data: theme
    } = useFetch({
        method: 'GET',
        url: '/clientTheme'
    });

    const { data: constants } = useFetch({
        method: 'GET',
        url: '/const'
    });

    const [ isGASet, setIsGASet ] = React.useState<boolean>(false);
    const [ isConfigFetched, setIsConfigFetched ] = React.useState<boolean>(false);
    const [ muiTheme, setMuiTheme ] = React.useState<ApplicationMuiTheme | undefined>(undefined);

    React.useEffect(() => {
        const fetchConfig = async (): Promise<void> => {
            try {
                await BrowserStorage.configurations(getAuthConfiguration);
                setIsConfigFetched(true);
            } catch (e) {
                console.error(e);
            }
        };

        fetchConfig();

        window.addEventListener('error', reportAppErrorEvent);
        window.addEventListener('unhandledrejection', reportAppErrorEvent);

        return () => {
            window.removeEventListener('error', reportAppErrorEvent);
            window.removeEventListener('unhandledrejection', reportAppErrorEvent);
        };
    }, []);

    React.useEffect(() => {
        if (theme && !isFetchingTheme) {
            const rawTheme = createMuiTheme(theme);
            setMuiTheme(rawTheme);
            browserSessionStore.setItem('is_sso', String(rawTheme.app?.clientTheme.features.Authentication.SSO));

            // Set favicons
            const favicon32: HTMLLinkElement = document.getElementById('favicon32') as HTMLLinkElement;
            const favicon16: HTMLLinkElement = document.getElementById('favicon16') as HTMLLinkElement;
            favicon32.href = rawTheme?.app.clientTheme.logoUrls?.ClientColorFaviconUrl ?? '';
            favicon16.href = rawTheme?.app.clientTheme.logoUrls?.ClientColorFaviconUrl ?? '';
        }
    }, [ isFetchingTheme, theme ]);

    React.useEffect(() => {
        if (muiTheme && Boolean(constants?.ENV) && !isGASet) {
            setGoogleAnalytics(isAdmin, muiTheme, constants.ENV);
            setIsGASet(true);
        }

        if (muiTheme && Boolean(constants?.APP_NAME)) {
            window.document.title = muiTheme.app.clientTheme.names.applicationName || constants.APP_NAME;
        }
    }, [ muiTheme, constants, isGASet ]);

    const DisplayedComponent = React.useMemo(() => {
        let ComponentToDisplay = <PageLoading />;

        if (muiTheme && constants && isGASet && isConfigFetched) {
            if (constants?.MODE === 'maint') {
                ComponentToDisplay = <MaintenancePage />;
            } else {
                ComponentToDisplay = isAdmin ? <AdminRoutes /> : <ClientRoutes />;
            }

            return (
                <Suspense fallback={<PageLoading />}>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={muiTheme}>
                            <ConstantsContext.Provider value={constants}>
                                {ComponentToDisplay}
                            </ConstantsContext.Provider>
                        </ThemeProvider>
                    </StyledEngineProvider>
                </Suspense>
            );
        }

        return ComponentToDisplay;
    }, [ muiTheme, constants, isGASet, isConfigFetched ]);

    return (DisplayedComponent);
};

export default App;